import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LayoutDashboard, BookOpen, ArrowRight, Pencil, Users, ClipboardList, Eye, Trash2, FileText, ExternalLink, ChevronRight } from 'lucide-react';
import {
  getTutorAssignedCourses,
  getTutorStudents,
  getTutorEnrollment,
  getTutorHomeworkAssignments,
  getTutorHomeworkSubmissions,
  gradeTutorHomeworkSubmission,
  createTutorHomeworkAssignment,
  updateTutorHomeworkAssignment,
  deleteTutorHomeworkAssignment,
  type TutorAssignedCourse,
  type TutorAssignedProgram,
  type TutorStudent,
  type TutorEnrollmentDetail,
  type TutorHomeworkAssignment,
  type TutorHomeworkSubmission,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { TutorDashboardSkeleton } from '@/components/skeletons/TutorDashboardSkeleton';
import { AdminTableSkeleton } from '@/components/skeletons/AdminTableSkeleton';

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

export function TutorDashboardPage() {
  const [courses, setCourses] = useState<TutorAssignedCourse[]>([]);
  const [programs, setPrograms] = useState<TutorAssignedProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'students' | 'homework' | 'documents'>('students');
  const [students, setStudents] = useState<TutorStudent[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState<string | null>(null);
  const [enrollmentDetail, setEnrollmentDetail] = useState<TutorEnrollmentDetail | null>(null);
  const [homeworkAssignments, setHomeworkAssignments] = useState<TutorHomeworkAssignment[]>([]);
  const [homeworkLoading, setHomeworkLoading] = useState(false);
  const [selectedHomework, setSelectedHomework] = useState<TutorHomeworkAssignment | null>(null);
  const [homeworkSubmissions, setHomeworkSubmissions] = useState<TutorHomeworkSubmission[]>([]);
  const [showHomeworkForm, setShowHomeworkForm] = useState(false);
  const [editingHomework, setEditingHomework] = useState<TutorHomeworkAssignment | null>(null);
  const [homeworkCourseId, setHomeworkCourseId] = useState<string>('');
  const [homeworkTitle, setHomeworkTitle] = useState<string>('');
  const [homeworkDescription, setHomeworkDescription] = useState<string>('');
  const [homeworkDueDate, setHomeworkDueDate] = useState<string>('');
  const [homeworkMaxScore, setHomeworkMaxScore] = useState<string>('100');
  const [homeworkXpReward, setHomeworkXpReward] = useState<string>('100');
  const [homeworkInstructions, setHomeworkInstructions] = useState<string>('');
  const [submissionModal, setSubmissionModal] = useState<{
    assignment: TutorHomeworkAssignment;
    submission: TutorHomeworkSubmission;
  } | null>(null);
  const [gradeScore, setGradeScore] = useState<string>('');
  const [gradeFeedback, setGradeFeedback] = useState<string>('');
  const [grading, setGrading] = useState(false);
  const [documents, setDocuments] = useState<Array<{
    assignmentId: string;
    assignmentTitle: string;
    courseId: string;
    courseTitle: string;
    learnerId: string;
    learnerName: string;
    submittedAt: string | null;
    fileUrl: string;
  }>>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [docFilterStudent, setDocFilterStudent] = useState<string>('');
  const [docFilterCourse, setDocFilterCourse] = useState<string>('');

  useEffect(() => {
    getTutorAssignedCourses()
      .then((data) => {
        setCourses(data.courses ?? []);
        setPrograms(data.programs ?? []);
      })
      .catch(() => {
        setCourses([]);
        setPrograms([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (activeTab !== 'students' || (courses.length === 0 && programs.length === 0)) return;
    setStudentsLoading(true);
    getTutorStudents()
      .then((data) => setStudents(data.students))
      .catch(() => setStudents([]))
      .finally(() => setStudentsLoading(false));
  }, [activeTab, courses.length, programs.length]);

  useEffect(() => {
    if (activeTab !== 'homework') return;
    setHomeworkLoading(true);
    getTutorHomeworkAssignments()
      .then((data) => setHomeworkAssignments(data.assignments ?? []))
      .catch(() => setHomeworkAssignments([]))
      .finally(() => setHomeworkLoading(false));
  }, [activeTab]);

  // Refetch assigned courses when opening the homework modal so the course selector is up-to-date
  useEffect(() => {
    if (!showHomeworkForm) return;
    getTutorAssignedCourses()
      .then((data) => {
        const list = data.courses ?? [];
        setCourses(list);
        setPrograms(data.programs ?? []);
        // If current selection is no longer valid (e.g. course has no modules), clear or pick first
        if (homeworkCourseId && !list.some((c) => c.courseId === homeworkCourseId)) {
          setHomeworkCourseId(list[0]?.courseId ?? '');
        }
      })
      .catch(() => {});
  }, [showHomeworkForm]);

  useEffect(() => {
    if (!selectedEnrollmentId) {
      setEnrollmentDetail(null);
      return;
    }
    getTutorEnrollment(selectedEnrollmentId)
      .then(setEnrollmentDetail)
      .catch(() => setEnrollmentDetail(null));
  }, [selectedEnrollmentId]);

  useEffect(() => {
    if (activeTab !== 'documents') return;
    setDocumentsLoading(true);
    getTutorHomeworkAssignments()
      .then(async (res) => {
        const list: Array<{
          assignmentId: string;
          assignmentTitle: string;
          courseId: string;
          courseTitle: string;
          learnerId: string;
          learnerName: string;
          submittedAt: string | null;
          fileUrl: string;
        }> = [];
        for (const a of res.assignments ?? []) {
          const { submissions } = await getTutorHomeworkSubmissions(a.id);
          for (const s of submissions ?? []) {
            for (const fileUrl of s.fileNames ?? []) {
              list.push({
                assignmentId: a.id,
                assignmentTitle: a.title,
                courseId: a.courseId,
                courseTitle: a.courseTitle,
                learnerId: s.learnerId,
                learnerName: s.learnerName,
                submittedAt: s.submittedAt,
                fileUrl,
              });
            }
          }
        }
        setDocuments(list);
      })
      .catch(() => setDocuments([]))
      .finally(() => setDocumentsLoading(false));
  }, [activeTab]);

  const filteredDocuments = documents.filter((d) => {
    if (docFilterStudent.trim() && !d.learnerName.toLowerCase().includes(docFilterStudent.trim().toLowerCase()))
      return false;
    if (docFilterCourse.trim() && !d.courseTitle.toLowerCase().includes(docFilterCourse.trim().toLowerCase()))
      return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-secondary/30">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
            <LayoutDashboard className="w-6 h-6 text-accent" />
          </div>
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Tutor dashboard</h1>
            <p className="text-sm text-muted-foreground">Your assigned programs and courses. Open one to manage content.</p>
          </div>
        </div>
        {loading ? (
          <TutorDashboardSkeleton />
        ) : courses.length === 0 && programs.length === 0 ? (
          <div className="rounded-2xl border border-border bg-card p-8 text-center">
            <BookOpen className="w-14 h-14 text-muted-foreground/50 mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-foreground mb-2">No courses or programs assigned</h2>
            <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
              You don't have any assignments yet. An admin can assign you to a program or course from the Tutors tab.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {programs.map((p) => (
                <Link
                  key={p.programId}
                  to={`/courses/${p.programSlug}`}
                  className="rounded-xl border border-border bg-card p-6 hover:shadow-lg hover:border-accent/30 transition-all flex items-center gap-4 group"
                >
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center shrink-0 group-hover:bg-accent/20">
                    <Pencil className="w-6 h-6 text-accent" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-foreground truncate">{p.programTitle}</p>
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-africa-blue/10 text-africa-blue border border-africa-blue/20 shrink-0">
                        Program
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">Manage entire program</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground shrink-0" />
                </Link>
              ))}
              {courses.map((c) => (
                <Link
                  key={c.courseId}
                  to={`/courses/${c.programSlug}/courses/${c.courseSlug}`}
                  className="rounded-xl border border-border bg-card p-6 hover:shadow-lg hover:border-accent/30 transition-all flex items-center gap-4 group"
                >
                  <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center shrink-0 group-hover:bg-accent/20">
                    <Pencil className="w-6 h-6 text-accent" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-foreground truncate">{c.courseTitle}</p>
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-africa-green/10 text-africa-green border border-africa-green/20 shrink-0">
                        Course
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{c.programTitle}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground shrink-0" />
                </Link>
              ))}
            </div>

            <div className="rounded-2xl border border-border bg-card overflow-hidden">
              <div className="flex border-b border-border">
                <button
                  type="button"
                  onClick={() => setActiveTab('students')}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'students'
                      ? 'text-accent border-b-2 border-accent bg-accent/5'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Users className="w-4 h-4" /> Students
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('homework')}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'homework'
                      ? 'text-accent border-b-2 border-accent bg-accent/5'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <ClipboardList className="w-4 h-4" /> Homework
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('documents')}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'documents'
                      ? 'text-accent border-b-2 border-accent bg-accent/5'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <FileText className="w-4 h-4" /> Documents
                </button>
              </div>
              <div className="p-6">
                {activeTab === 'students' && (
                  <>
                    {studentsLoading ? (
                      <AdminTableSkeleton rows={6} cols={7} />
                    ) : students.length === 0 ? (
                      <div className="py-12 text-center text-muted-foreground">
                        No students enrolled in your assigned courses yet.
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="text-left font-semibold px-3 py-2 text-sm">Student</th>
                              <th className="text-left font-semibold px-3 py-2 text-sm hidden sm:table-cell">Email</th>
                              <th className="text-left font-semibold px-3 py-2 text-sm">Course</th>
                              <th className="text-left font-semibold px-3 py-2 text-sm">Status</th>
                              <th className="text-left font-semibold px-3 py-2 text-sm">Progress</th>
                              <th className="text-left font-semibold px-3 py-2 text-sm hidden md:table-cell">Enrolled</th>
                              <th className="text-right font-semibold px-3 py-2 text-sm">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {students.map((s) => (
                              <tr key={s.enrollmentId} className="border-b border-border hover:bg-secondary/30">
                                <td className="px-3 py-2 font-medium text-sm">{s.learnerName}</td>
                                <td className="px-3 py-2 text-sm text-muted-foreground hidden sm:table-cell">{s.learnerEmail}</td>
                                <td className="px-3 py-2 text-sm">{s.courseTitle}</td>
                                <td className="px-3 py-2">
                                  <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-africa-green/10 text-africa-green border border-africa-green/20">
                                    {s.status}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  {s.totalLessons > 0
                                    ? `${s.completedCount}/${s.totalLessons} (${Math.round((s.completedCount / s.totalLessons) * 100)}%)`
                                    : '—'}
                                </td>
                                <td className="px-3 py-2 text-sm text-muted-foreground hidden md:table-cell">{formatDate(s.enrolledAt)}</td>
                                <td className="px-3 py-2 text-right">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="gap-1"
                                    onClick={() => setSelectedEnrollmentId(s.enrollmentId)}
                                  >
                                    <Eye className="w-4 h-4" /> View
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
                {activeTab === 'homework' && (
                  <div className="space-y-4">
                    {homeworkLoading ? (
                      <div className="py-16 text-center text-muted-foreground">
                        Loading homework…
                      </div>
                    ) : selectedHomework ? (
                      <>
                        <div className="flex items-center gap-3 mb-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedHomework(null);
                              setHomeworkSubmissions([]);
                            }}
                            className="gap-1"
                          >
                            <ArrowRight className="-rotate-180 w-4 h-4" /> Back
                          </Button>
                          <div className="flex-1">
                            <h3 className="font-display font-semibold text-foreground text-lg">
                              {selectedHomework.title}
                            </h3>
                            <p className="text-xs text-muted-foreground">
                              {selectedHomework.courseTitle}
                              {selectedHomework.dueDate && <> · Due {selectedHomework.dueDate}</>}
                            </p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          {homeworkSubmissions.length === 0 ? (
                            <div className="py-12 text-center text-muted-foreground">
                              <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-30" />
                              <p>No submissions yet for this assignment.</p>
                            </div>
                          ) : (
                            homeworkSubmissions.map((sub) => (
                              <button
                                key={sub.id}
                                type="button"
                                onClick={() => {
                                  if (!selectedHomework) return;
                                  setSubmissionModal({ assignment: selectedHomework, submission: sub });
                                  setGradeScore(sub.score != null ? String(sub.score) : '');
                                  setGradeFeedback(sub.feedback ?? '');
                                }}
                                className="w-full text-left border border-border rounded-lg p-3 flex items-center gap-3 hover:border-accent/40 hover:bg-secondary/30 transition-all"
                              >
                                <div className="w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 bg-secondary text-muted-foreground">
                                  {sub.score != null ? `${sub.score}%` : '—'}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-foreground truncate">
                                    {sub.learnerName}
                                  </p>
                                  <p className="text-xs text-muted-foreground truncate">
                                    {sub.submittedAt
                                      ? `Submitted ${new Date(sub.submittedAt).toLocaleDateString()}`
                                      : 'Not submitted'}
                                  </p>
                                </div>
                                <span className="text-xs text-muted-foreground">View & grade</span>
                                <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                              </button>
                            ))
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <ClipboardList className="w-4 h-4 text-accent" />
                            <h3 className="text-sm font-display font-semibold text-foreground">
                              Homework assignments ({homeworkAssignments.length})
                            </h3>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            className="bg-accent text-accent-foreground hover:bg-accent/90 gap-1.5"
                            onClick={() => {
                              if (courses.length === 0) return;
                              setEditingHomework(null);
                              setHomeworkCourseId(courses[0].courseId);
                              setHomeworkTitle('');
                              setHomeworkDescription('');
                              setHomeworkDueDate('');
                              setHomeworkMaxScore('100');
                              setHomeworkXpReward('100');
                              setHomeworkInstructions('');
                              setShowHomeworkForm(true);
                            }}
                          >
                            <Pencil className="w-3 h-3" /> New assignment
                          </Button>
                        </div>
                        {homeworkAssignments.length === 0 ? (
                          <div className="py-12 text-center text-muted-foreground">
                            <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-30" />
                            <p>No homework assignments yet.</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {homeworkAssignments.map((a) => (
                              <div
                                key={a.id}
                                className="w-full border border-border rounded-lg bg-card px-4 py-3 flex items-center gap-3 hover:border-accent/40 hover:bg-secondary/40 transition-all"
                              >
                                <button
                                  type="button"
                                  onClick={async () => {
                                    setSelectedHomework(a);
                                    const data = await getTutorHomeworkSubmissions(a.id);
                                    setHomeworkSubmissions(data.submissions ?? []);
                                  }}
                                  className="flex items-center gap-3 flex-1 min-w-0 text-left"
                                >
                                  <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                                    <ClipboardList className="w-4 h-4 text-accent" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-foreground truncate">
                                      {a.title}
                                    </p>
                                    <p className="text-xs text-muted-foreground truncate">
                                      {a.courseTitle || 'Course'}
                                      {a.dueDate && <> · Due {a.dueDate}</>}
                                    </p>
                                  </div>
                                  <div className="flex flex-col items-end gap-0.5 shrink-0 text-[10px] text-muted-foreground">
                                    <span>{a.submissionsTotal} submissions</span>
                                    <span>{a.submissionsPending} pending</span>
                                  </div>
                                </button>
                                <div className="flex items-center gap-1 shrink-0">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={async () => {
                                      setEditingHomework(a);
                                      setHomeworkCourseId(a.courseId);
                                      setHomeworkTitle(a.title);
                                      setHomeworkDescription('');
                                      setHomeworkDueDate(a.dueDate ?? '');
                                      setHomeworkMaxScore(String(a.maxScore ?? 100));
                                      setHomeworkXpReward(String(a.xpReward ?? 100));
                                      setHomeworkInstructions('');
                                      setShowHomeworkForm(true);
                                    }}
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-destructive"
                                    onClick={async () => {
                                      const confirmed = window.confirm(
                                        'Delete this assignment? This cannot be undone.'
                                      );
                                      if (!confirmed) return;
                                      await deleteTutorHomeworkAssignment(a.id);
                                      setHomeworkAssignments((prev) =>
                                        prev.filter((row) => row.id !== a.id)
                                      );
                                    }}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
                {activeTab === 'documents' && (
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <input
                        type="text"
                        placeholder="Filter by student name"
                        value={docFilterStudent}
                        onChange={(e) => setDocFilterStudent(e.target.value)}
                        className="flex h-9 w-full max-w-[200px] rounded-md border border-input bg-background px-3 py-1 text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Filter by course"
                        value={docFilterCourse}
                        onChange={(e) => setDocFilterCourse(e.target.value)}
                        className="flex h-9 w-full max-w-[200px] rounded-md border border-input bg-background px-3 py-1 text-sm"
                      />
                    </div>
                    {documentsLoading ? (
                      <AdminTableSkeleton rows={5} cols={5} />
                    ) : filteredDocuments.length === 0 ? (
                      <div className="py-12 text-center text-muted-foreground">
                        <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p>{documents.length === 0 ? 'No documents from homework submissions yet.' : 'No documents match the filters.'}</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto border border-border rounded-lg">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border bg-muted/50">
                              <th className="text-left font-semibold px-3 py-2">Student</th>
                              <th className="text-left font-semibold px-3 py-2">Course</th>
                              <th className="text-left font-semibold px-3 py-2">Assignment</th>
                              <th className="text-left font-semibold px-3 py-2">Submitted</th>
                              <th className="text-left font-semibold px-3 py-2">File</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredDocuments.map((d, i) => (
                              <tr key={`${d.assignmentId}-${d.learnerId}-${i}`} className="border-b border-border last:border-0">
                                <td className="px-3 py-2 text-foreground">{d.learnerName}</td>
                                <td className="px-3 py-2 text-muted-foreground">{d.courseTitle}</td>
                                <td className="px-3 py-2 text-foreground">{d.assignmentTitle}</td>
                                <td className="px-3 py-2 text-muted-foreground">
                                  {d.submittedAt ? formatDate(d.submittedAt) : '—'}
                                </td>
                                <td className="px-3 py-2">
                                  <a
                                    href={d.fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-accent hover:underline"
                                  >
                                    Open <ExternalLink className="w-3 h-3" />
                                  </a>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Submission detail modal (view homework + grade) */}
      {submissionModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          onClick={() => setSubmissionModal(null)}
        >
          <div
            className="bg-card border border-border rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-display font-bold text-foreground">Submission</h2>
                <Button type="button" variant="ghost" size="sm" onClick={() => setSubmissionModal(null)}>
                  Close
                </Button>
              </div>
              <div className="bg-secondary/50 rounded-lg p-4 space-y-1">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Assignment</p>
                <p className="font-medium text-foreground">{submissionModal.assignment.title}</p>
                <p className="text-sm text-muted-foreground">
                  {submissionModal.assignment.courseTitle}
                  {submissionModal.assignment.dueDate && <> · Due {submissionModal.assignment.dueDate}</>}
                </p>
              </div>
              {submissionModal.assignment.instructions && (
                <div className="bg-secondary/50 rounded-lg p-4 space-y-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Instructions</p>
                  <p className="text-sm text-foreground whitespace-pre-line">{submissionModal.assignment.instructions}</p>
                </div>
              )}
              <div className="bg-secondary/50 rounded-lg p-4 space-y-1">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Student</p>
                <p className="font-medium text-foreground">{submissionModal.submission.learnerName}</p>
                <p className="text-sm text-muted-foreground">
                  {submissionModal.submission.submittedAt
                    ? `Submitted ${formatDate(submissionModal.submission.submittedAt)}`
                    : 'Not submitted'}
                </p>
              </div>
              <div className="bg-secondary/50 rounded-lg p-4 space-y-1">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Response</p>
                <p className="text-sm text-foreground whitespace-pre-line">
                  {submissionModal.submission.content || '—'}
                </p>
              </div>
              {submissionModal.submission.fileNames.length > 0 && (
                <div className="bg-secondary/50 rounded-lg p-4 space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Attached files</p>
                  <div className="flex flex-wrap gap-2">
                    {submissionModal.submission.fileNames.map((url, i) => {
                      const label = url.split('/').pop() ?? `File ${i + 1}`;
                      return (
                        <a
                          key={i}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-border bg-background text-sm text-accent hover:underline"
                        >
                          {label} <ExternalLink className="w-3 h-3" />
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="border-t border-border pt-4 space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Grade</p>
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Score (0–100)</label>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={gradeScore}
                      onChange={(e) => setGradeScore(e.target.value)}
                      className="flex h-9 w-full max-w-[120px] rounded-md border border-input bg-background px-3 py-1 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Feedback</label>
                    <Textarea
                      rows={3}
                      value={gradeFeedback}
                      onChange={(e) => setGradeFeedback(e.target.value)}
                      placeholder="Optional feedback for the student"
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setSubmissionModal(null)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="bg-accent text-accent-foreground hover:bg-accent/90"
                    disabled={grading || !gradeScore.trim()}
                    onClick={async () => {
                      const n = Number(gradeScore);
                      if (!Number.isFinite(n) || n < 0 || n > 100) return;
                      setGrading(true);
                      try {
                        await gradeTutorHomeworkSubmission(submissionModal.submission.id, {
                          score: n,
                          feedback: gradeFeedback.trim() || undefined,
                        });
                        const data = await getTutorHomeworkSubmissions(submissionModal.assignment.id);
                        setHomeworkSubmissions(data.submissions ?? []);
                        setSubmissionModal(null);
                      } finally {
                        setGrading(false);
                      }
                    }}
                  >
                    {grading ? 'Saving…' : 'Submit grade'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Student enrollment detail modal */}
      {(selectedEnrollmentId || enrollmentDetail) && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          onClick={() => { setSelectedEnrollmentId(null); setEnrollmentDetail(null); }}
        >
          <div
            className="bg-card border border-border rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-display font-bold text-foreground">Enrollment details</h2>
                <Button type="button" variant="ghost" size="sm" onClick={() => { setSelectedEnrollmentId(null); setEnrollmentDetail(null); }}>Close</Button>
              </div>
              {enrollmentDetail ? (
                <div className="space-y-4">
                  <div className="bg-secondary/50 rounded-lg p-4 space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Student</p>
                    <p className="font-medium text-foreground">{enrollmentDetail.learnerName}</p>
                    <p className="text-sm text-muted-foreground">{enrollmentDetail.learnerEmail}</p>
                    {enrollmentDetail.learnerPhone && (
                      <p className="text-sm text-muted-foreground">{enrollmentDetail.learnerPhone}</p>
                    )}
                  </div>
                  <div className="bg-secondary/50 rounded-lg p-4 space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Course & status</p>
                    <p className="font-medium text-foreground">{enrollmentDetail.courseTitle}</p>
                    <p className="text-sm text-muted-foreground">{enrollmentDetail.programTitle}</p>
                    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-africa-green/10 text-africa-green border border-africa-green/20">
                      {enrollmentDetail.status}
                    </span>
                  </div>
                  <div className="bg-secondary/50 rounded-lg p-4 space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Progress</p>
                    <p className="text-sm">
                      {enrollmentDetail.progress.totalLessons > 0 ? (
                        <>
                          <span className="font-medium">{enrollmentDetail.progress.completedCount}</span> of {enrollmentDetail.progress.totalLessons} lessons completed
                          <span className="text-muted-foreground"> ({enrollmentDetail.progress.percent}%)</span>
                        </>
                      ) : (
                        <span className="text-muted-foreground">No lessons in this course yet.</span>
                      )}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground">Enrolled {formatDate(enrollmentDetail.enrolledAt)}</p>
                </div>
              ) : (
                <div className="py-8 text-center text-muted-foreground">Loading…</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Homework create/edit modal */}
      {showHomeworkForm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          onClick={() => setShowHomeworkForm(false)}
        >
          <div
            className="bg-card border border-border rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-display font-bold text-foreground">
                    {editingHomework ? 'Edit homework assignment' : 'Create homework assignment'}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-1">
                    Define the brief, scoring, and XP for this assignment. Students will see this on
                    their homework page.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowHomeworkForm(false)}
                >
                  Close
                </Button>
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Course</p>
                    <p className="text-[10px] text-muted-foreground mb-1">
                      Only courses with at least one module are listed.
                    </p>
                    <select
                      value={homeworkCourseId}
                      onChange={(e) => setHomeworkCourseId(e.target.value)}
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-xs"
                    >
                      <option value="">Select course</option>
                      {courses.map((c) => (
                        <option key={c.courseId} value={c.courseId}>
                          {c.programTitle ? `${c.courseTitle} (${c.programTitle})` : c.courseTitle}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Due date</p>
                    <input
                      type="date"
                      value={homeworkDueDate}
                      onChange={(e) => setHomeworkDueDate(e.target.value)}
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-xs"
                    />
                  </div>
                </div>

                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Title</p>
                  <input
                    type="text"
                    value={homeworkTitle}
                    onChange={(e) => setHomeworkTitle(e.target.value)}
                    placeholder="e.g. Market analysis report"
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                  />
                </div>

                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Short description</p>
                  <textarea
                    value={homeworkDescription}
                    onChange={(e) => setHomeworkDescription(e.target.value)}
                    rows={3}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-xs"
                    placeholder="A brief overview of what learners are expected to deliver."
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Max score</p>
                    <input
                      type="number"
                      min={1}
                      max={1000}
                      value={homeworkMaxScore}
                      onChange={(e) => setHomeworkMaxScore(e.target.value)}
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-xs"
                    />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">XP reward</p>
                    <input
                      type="number"
                      min={0}
                      max={10000}
                      value={homeworkXpReward}
                      onChange={(e) => setHomeworkXpReward(e.target.value)}
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-xs"
                    />
                  </div>
                </div>

                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Instructions</p>
                  <textarea
                    value={homeworkInstructions}
                    onChange={(e) => setHomeworkInstructions(e.target.value)}
                    rows={4}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-xs"
                    placeholder="Step-by-step instructions, submission format, links to reference material, etc."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowHomeworkForm(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="bg-accent text-accent-foreground hover:bg-accent/90 gap-1.5"
                  disabled={!homeworkCourseId || !homeworkTitle.trim()}
                  onClick={async () => {
                    const maxScoreNumber = Number(homeworkMaxScore) || 100;
                    const xpNumber = Number(homeworkXpReward) || 100;
                    if (editingHomework) {
                      const updated = await updateTutorHomeworkAssignment(editingHomework.id, {
                        title: homeworkTitle.trim(),
                        dueDate: homeworkDueDate || null,
                        maxScore: maxScoreNumber,
                        xpReward: xpNumber,
                        instructions: homeworkInstructions || undefined,
                      });
                      setHomeworkAssignments((prev) =>
                        prev.map((row) =>
                          row.id === editingHomework.id
                            ? {
                                ...row,
                                title: updated.title,
                                dueDate: updated.dueDate,
                                maxScore: updated.maxScore,
                                xpReward: updated.xpReward,
                              }
                            : row
                        )
                      );
                    } else {
                      const created = await createTutorHomeworkAssignment({
                        courseId: homeworkCourseId,
                        title: homeworkTitle.trim(),
                        description: homeworkDescription || undefined,
                        dueDate: homeworkDueDate || undefined,
                        maxScore: maxScoreNumber,
                        xpReward: xpNumber,
                        instructions: homeworkInstructions || undefined,
                      });
                      setHomeworkAssignments((prev) => [created, ...prev]);
                    }
                    setShowHomeworkForm(false);
                  }}
                >
                  {editingHomework ? 'Save changes' : 'Create assignment'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
